import { apiv1 as api } from '@/adapters';
import type { RoleModel, RolePermissionModel } from '@/adapters/apiv1.g';
import {
  extractAccepted,
  extractBoolean,
  extractPageModels,
  unwrapData,
} from '@/adapters/shared';
import { extractRole } from '@/adapters/shared/roles/extract-role';
import type { Role } from '@/types/core/role';
import type { Page, PageRequest } from '@/types/shared/page';
import { toApiQuery } from '@/types/shared/page';
import { type UUID, newUUID } from '@/utils/uuid';

export const roleService = {
  listRoles: async (
    tenantId: UUID,
    query?: PageRequest
  ): Promise<Page<Role>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listRoles(tenantId, apiQuery);
    const rawModels = extractPageModels<RoleModel>(res);
    const models = rawModels.map(extractRole);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return { items: models, next, prev, limit: query?.limit };
  },

  /** Find a specific role by id */
  findRole: async (tenantId: UUID, roleId: UUID): Promise<Role | null> => {
    const res = await api.findRole(tenantId, roleId);
    const model = unwrapData(res);
    if (!model) return null;
    return extractRole(model);
  },

  createRole: async (tenantId: UUID, role: Role): Promise<Role> => {
    const roleId = newUUID();
    const res = await api.reconcileRole(tenantId, roleId, {
      name: role.name ?? '',
      description: role.description ?? '',
    });
    if (!extractAccepted(res)) {
      throw new Error('Failed to create role');
    }
    return { ...role, roleId };
  },

  /** Update an existing role */
  updateRole: async (
    tenantId: UUID,
    roleId: UUID,
    updates: Partial<Omit<Role, 'roleId'>>
  ): Promise<Role> => {
    // Fetch existing role to merge with updates (reconcile is full replacement)
    const existingRole = await roleService.findRole(tenantId, roleId);
    if (!existingRole) {
      throw new Error('Role not found');
    }

    const res = await api.reconcileRole(tenantId, roleId, {
      name: updates.name ?? existingRole.name ?? '',
      description: updates.description ?? existingRole.description ?? '',
    });
    if (!extractAccepted(res)) {
      throw new Error('Failed to update role');
    }
    return { ...existingRole, ...updates };
  },

  /** Delete a role */
  deleteRole: async (tenantId: UUID, roleId: UUID): Promise<boolean> => {
    const res = await api.removeRole(tenantId, roleId);
    return extractBoolean(res);
  },

  /** Grant a permission to a role (permission_id = role_permission_id from list, or SHA1(role_id, permission)) */
  grantRolePermission: async (
    tenantId: UUID,
    roleId: UUID,
    permissionId: UUID,
    body: { permission: string }
  ): Promise<boolean> => {
    if (!body.permission?.includes(':')) {
      throw new Error(
        `Invalid permission format: ${body.permission} (expected "scope:resource:operation")`
      );
    }
    const res = await api.grantRolePermission(tenantId, roleId, permissionId, {
      permission: body.permission,
    });
    return extractAccepted(res);
  },

  /** List all permissions for a specific role */
  listRolePermissions: async (
    tenantId: UUID,
    roleId: UUID,
    query?: PageRequest
  ): Promise<Page<RolePermissionModel>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listRolePermissions(tenantId, roleId, apiQuery);
    const models = extractPageModels<RolePermissionModel>(res);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return { items: models, next, prev, limit: query?.limit };
  },

  /** Revoke a specific permission from a role by role_permission_id */
  revokeRolePermission: async (
    tenantId: UUID,
    roleId: UUID,
    permissionId: UUID
  ): Promise<boolean> => {
    const res = await api.removeRolePermission(tenantId, roleId, permissionId);
    return extractBoolean(res);
  },
};
